/**
 * Deep-merges shared expedition config (base) with map config (overrides).
 * Map config wins on all key conflicts.
 * map_id and global_config are always preserved from mapData.
 */
export class SharedConfigMerge {

    static mergeSharedIntoMap(sharedData, mapData) {
        const cleanShared = JSON.parse(JSON.stringify(sharedData || {}));
        const cleanMap = JSON.parse(JSON.stringify(mapData || {}));
        delete cleanShared['_init'];

        // Items are identified by item.key (kind + level), not by the outer object key.
        SharedConfigMerge._normalizeCollection(cleanShared, 'items', SharedConfigMerge._normalizeItemsCollection);
        SharedConfigMerge._normalizeCollection(cleanMap, 'items', SharedConfigMerge._normalizeItemsCollection);
        SharedConfigMerge._normalizeCollection(cleanShared, 'quests', SharedConfigMerge._normalizeUidCollection);
        SharedConfigMerge._normalizeCollection(cleanMap, 'quests', SharedConfigMerge._normalizeUidCollection);
        SharedConfigMerge._normalizeCollection(cleanShared, 'rewards', SharedConfigMerge._normalizeUidCollection);
        SharedConfigMerge._normalizeCollection(cleanMap, 'rewards', SharedConfigMerge._normalizeUidCollection);

        const result = SharedConfigMerge._deepMerge(
            cleanShared,
            cleanMap
        );
        if (mapData && mapData['map_id'] !== undefined) {
            result['map_id'] = mapData['map_id'];
        }
        if (mapData && mapData['global_config'] !== undefined) {
            result['global_config'] = mapData['global_config'];
        } else {
            delete result['global_config'];
        }
        return result;
    }

    static _normalizeItemsCollection(items) {
        if (items === null || typeof items !== 'object' || Array.isArray(items)) {
            return items;
        }

        return Object.entries(items).reduce((result, [outerKey, value]) => {
            const normalizedKey = SharedConfigMerge._getItemKey(outerKey, value);

            if (
                result[normalizedKey] !== null &&
                typeof result[normalizedKey] === 'object' &&
                !Array.isArray(result[normalizedKey]) &&
                value !== null &&
                typeof value === 'object' &&
                !Array.isArray(value)
            ) {
                result[normalizedKey] = SharedConfigMerge._deepMerge(result[normalizedKey], value);
            } else {
                result[normalizedKey] = value;
            }

            return result;
        }, {});
    }

    static _getItemKey(fallbackKey, value) {
        const kind = value?.key?.kind;
        const level = value?.key?.level;

        if (typeof kind === 'string' && level !== undefined && level !== null) {
            return `${kind}_${level}`;
        }

        return fallbackKey;
    }

    static _normalizeCollection(target, key, normalizeFn) {
        if (!Object.prototype.hasOwnProperty.call(target, key)) {
            return;
        }

        target[key] = normalizeFn.call(SharedConfigMerge, target[key]);
    }

    static _normalizeUidCollection(items) {
        if (items === null || typeof items !== 'object' || Array.isArray(items)) {
            return items;
        }

        return Object.entries(items).reduce((result, [outerKey, value]) => {
            const normalizedKey = SharedConfigMerge._getUidKey(outerKey, value);

            if (
                result[normalizedKey] !== null &&
                typeof result[normalizedKey] === 'object' &&
                !Array.isArray(result[normalizedKey]) &&
                value !== null &&
                typeof value === 'object' &&
                !Array.isArray(value)
            ) {
                result[normalizedKey] = SharedConfigMerge._deepMerge(result[normalizedKey], value);
            } else {
                result[normalizedKey] = value;
            }

            return result;
        }, {});
    }

    static _getUidKey(fallbackKey, value) {
        const uid = value?.uid;

        if (typeof uid === 'string' && uid.length > 0) {
            return uid;
        }

        return fallbackKey;
    }

    static _deepMerge(base, override) {
        const result = Object.assign({}, base);
        for (const key of Object.keys(override)) {
            if (
                override[key] !== null &&
                typeof override[key] === 'object' &&
                !Array.isArray(override[key]) &&
                base[key] !== null &&
                typeof base[key] === 'object' &&
                !Array.isArray(base[key])
            ) {
                result[key] = SharedConfigMerge._deepMerge(base[key], override[key]);
            } else {
                result[key] = override[key];
            }
        }
        return result;
    }
}
